"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { PERMISSIONS_UPDATED_EVENT, savePermissions, saveModules, saveVoiceEnabled, saveSuporteAvailable } from "@/lib/permissions";
import { api } from "@/lib/api";

// Dedup: só uma requisição /auth/permissions em vôo por vez.
// useLivePermissions é chamado de várias layouts/sidebar simultaneamente;
// sem dedup viraria N requisições redundantes a cada focus/rota.
let inflight: Promise<void> | null = null;

// #123.4 (Onda 4): throttle TEMPORAL. O dedup `inflight` só corta chamadas
// concorrentes (em vôo ao mesmo tempo); navegação rápida entre rotas ou
// alt-tab repetido dispara GETs /auth/permissions em série. Permissões mudam
// raramente (admin edita cargo) — ignorar re-fetches dentro de 5s da última
// corta o tráfego ocioso sem perder freshness perceptível (uma mudança real
// reflete no próximo trigger após a janela). Mudança imediata (login, edição
// do próprio cargo) já chega por savePermissions direto, não por aqui.
const REFRESH_THROTTLE_MS = 5_000;
let lastFetchAt = 0;

/**
 * Re-busca /auth/permissions e atualiza o localStorage via savePermissions
 * (que dispara PERMISSIONS_UPDATED_EVENT → re-render de todos os useLivePermissions).
 * Silenciosa em erro (ex: 401 após logout; ou desconexão momentânea).
 * Throttled em 5s (passe `force` pra ignorar a janela quando precisar de freshness garantida).
 */
export function refreshPermissions(opts?: { force?: boolean }): Promise<void> {
  if (inflight) return inflight;
  if (!opts?.force && Date.now() - lastFetchAt < REFRESH_THROTTLE_MS) {
    return Promise.resolve();
  }
  lastFetchAt = Date.now();
  inflight = (async () => {
    try {
      const perms = await api.request<{
        permissions: string[];
        denied?: string[];
        isGkAdmin: boolean;
        activeModules?: string[];
        voiceEnabled?: boolean;
        suporteAvailable?: boolean;
      }>("/auth/permissions");
      savePermissions(perms.permissions, perms.isGkAdmin, perms.denied ?? []);
      saveModules(perms.activeModules ?? []);
      saveVoiceEnabled(perms.voiceEnabled ?? false);
      saveSuporteAvailable(perms.suporteAvailable ?? false);
    } catch {
      // silencioso — tenta de novo no próximo focus/rota
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * Hook que mantém o componente sincronizado com o estado atual das
 * permissões. Re-renderiza quando:
 *  - savePermissions dispara o evento (login, refresh manual)
 *  - outra aba muda o localStorage (storage event)
 * E DISPARA refresh quando:
 *  - a aba ganha foco / fica visível (admin mudou cargo → user volta pra aba)
 *  - a rota muda (navegou pra outro link)
 *
 * Retorna um version number que muda a cada atualização (útil pra forçar
 * re-render de componentes memoizados).
 */
export function useLivePermissions(): number {
  const [version, setVersion] = useState(0);
  const pathname = usePathname();

  // Escuta eventos locais e de storage pra re-renderizar
  useEffect(() => {
    const onUpdate = () => setVersion((v) => v + 1);
    window.addEventListener(PERMISSIONS_UPDATED_EVENT, onUpdate);
    window.addEventListener("storage", onUpdate);
    return () => {
      window.removeEventListener(PERMISSIONS_UPDATED_EVENT, onUpdate);
      window.removeEventListener("storage", onUpdate);
    };
  }, []);

  // Re-fetch quando a aba volta a ficar visível (focus / visibilitychange)
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") refreshPermissions();
    }
    function onFocus() {
      refreshPermissions();
    }
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Re-fetch a cada mudança de rota
  useEffect(() => {
    refreshPermissions();
  }, [pathname]);

  return version;
}
